import api from './api'
import type {
  JobShift,
  ShiftApplication,
  WorkerAvailabilityWindow,
  WorkerProfileDetail,
  WorkerProfileDocument,
  WorkerReliabilityProfile,
  WorkerUnavailabilityWindow,
} from '../types/access'

export interface WorkerAssignment {
  mapLocation?: {
    available: boolean
    latitude: number | null
    longitude: number | null
    precision: 'exact' | 'approximate' | 'hidden'
    displayLabel: string | null
    locationId: string | null
    geofence?: { type: 'circle' | 'polygon'; centerLatitude?: number | null; centerLongitude?: number | null; radiusMeters?: number | null; polygon?: Array<[number, number]> | null } | null
    navigationAllowed: boolean
  }
  distanceLabel?: string | null
  jobMapFeatureEnabled?: boolean
  id: string
  job_title?: string | null
  starts_at?: string | null
  ends_at?: string | null
  startsAt?: string | null
  endsAt?: string | null
  timezone?: string | null
  assignmentStatus?: string | null
  assignment_status?: string | null
  timeState?: string | null
  punchWindowReason?: string | null
  currentPunchState?: string | null
  hasActiveWorkerShift?: boolean
  canPunchNow?: boolean
  /** Backend flag: this assignment already had a completed shift today. */
  canResumeNow?: boolean
  can_resume_now?: boolean
  canMarkOnWay?: boolean
  canReportRunningLate?: boolean
  canReportCannotAttend?: boolean
  canRequestCancelAssignment?: boolean
  canStartBreak?: boolean
  canEndBreak?: boolean
  canPunchOut?: boolean
  onTheWayAt?: string | null
  cancellationRequest?: { status?: string; reason?: string } | null
  location_id?: string | null
  location_name?: string | null
  location_address?: string | null
  latitude?: number | null
  longitude?: number | null
  destination?: { locationId?: string | null; name?: string | null; displayAddress?: string | null; latitude?: number | null; longitude?: number | null; timezone?: string | null; addressStatus?: string | null; routingAvailable?: boolean }
  matching?: { workerLocationKnown?: boolean; jobLocationKnown?: boolean; distanceMeters?: number | null; eligible?: boolean; reason?: string | null }
  punchGeofence?: { configured?: boolean; type?: 'circle' | 'polygon' | null; radiusMeters?: number | null; enforcementMode?: string }
  work_mode?: string | null
  workMode?: string | null
  company_org_name?: string | null
  supplying_agency_org_name?: string | null
  agency_org_name?: string | null
  payLabel?: string | null
  pay_label?: string | null
  instructions?: string | null
  required_skill_names?: string[]
  documentEligibility?: { eligible?: boolean; issues?: Array<{ label?: string; reason?: string }> } | null
  companyBranding?: { displayName?: string | null; logoUrl?: string | null } | null
  agencyBranding?: { displayName?: string | null; logoUrl?: string | null } | null
}

export interface WorkerTimesheet {
  shiftId: string
  locationName?: string | null
  companyName?: string | null
  agencyName?: string | null
  payLabel?: string | null
  payVisibility?: 'visible' | 'masked'
  shiftDate?: string | null
  submittedStartTime?: string | null
  submittedEndTime?: string | null
  submittedBreakMinutes: number
  submittedTotalHours: number
  approvalStatus: string
  adminAdjustmentReason?: string | null
  adminAdjustedTotalHours?: number | null
  finalBreakMinutes?: number | null
  finalTotalHours?: number | null
  rejectionReason?: string | null
  workerDisputeMessage?: string | null
  payrollLocked: boolean
  approvedAt?: string | null
  workerDisputedAt?: string | null
  disputeResolutionNote?: string | null
  disputeResolvedAt?: string | null
  approvalSummary?: {
    status: string
    submittedAt?: string | null
    approvedAt?: string | null
    adjustedAt?: string | null
    rejectedAt?: string | null
    resolvedAt?: string | null
    payrollLocked: boolean
  }
  approvalHistory?: Array<{
    action: string
    occurredAt: string
    actorDisplayName?: string | null
    workerVisibleMessage?: string | null
  }>
  settlement?: {
    state: 'not_ready' | 'ready' | 'locked' | 'exported' | 'settled' | 'unknown'
    exportedAt?: string | null
  }
}

export interface WorkerMessageThread {
  id: string
  title?: string | null
  status: string
  canReply: boolean
  unreadCount: number
  lastMessageBody?: string | null
  lastMessageAt?: string | null
  scopeType?: string | null
  scopeId?: string | null
}

export interface WorkerMessage { id: string; body: string; senderName?: string | null; createdAt?: string | null }
export interface WorkerNotification { id: string; title?: string; message?: string; body?: string; readAt?: string | null; createdAt?: string; actionUrl?: string | null; metadata?: Record<string, unknown> }
export interface WorkerDecision { id: string; decision_type: string; outcome: string; decision_code: string; summary: string; reasons: Array<{ code: string; message: string; canAppeal?: boolean }>; appealable: boolean; created_at: string }
export interface WorkerDecisionAppeal { id: string; decision_id: string; decision_type?: string; status: string; worker_message: string; decision_reason?: string | null; submitted_at: string; summary?: string; reasons?: Array<{ message?: string }> }
export interface WorkerLocationPrivacyStatus { trackingActive: boolean; purposeCode?: string | null; reason: string; jobAssignmentId?: string | null; locationId?: string | null; locationName?: string | null; trackingStopsAt?: string | null; lastAcceptedLocationAt?: string | null; settings: { breadcrumbIntervalSeconds: number; retentionDays: number; workerLocationHistoryVisible: boolean } }
export interface WorkerSkillProgression { claimSource?: string | null; verificationStatus?: string; skillId?: string; skillName?: string; currentLevel?: string; isCertified?: boolean; completedShiftCount?: number; completedVerifiedHours?: number; mentorshipMinutesCompleted?: number; nextLevel?: { label?: string }; nextLevelRequirements?: Array<{ message?: string }>; recommendedActions?: Array<{ label?: string }> }

const data = <T>(response: { data?: T }) => response.data as T
const idempotencyKey = (action: string, assignmentId: string) => `${action}:${assignmentId}:${Date.now()}:${Math.random().toString(36).slice(2)}`

export const workerService = {
  linkOrganization: async (inviteCode: string) => data<any>(await api.post('/api/auth/worker/link-org', { inviteCode })),
  getTodayAssignments: async () => (data<any>(await api.get('/api/workers/me/assignments/today')).assignments || []) as WorkerAssignment[],
  getUpcomingAssignments: async () => (data<any>(await api.get('/api/workers/me/assignments/upcoming', { params: { limit: 50 } })).assignments || []) as WorkerAssignment[],
  getHistory: async () => (data<any>(await api.get('/api/workers/me/jobs/history', { params: { limit: 50 } })).history || []) as WorkerAssignment[],
  getPunchStatus: async () => data<any>(await api.get('/api/workers/me/punch-status')),
  getOpenShifts: async () => (data<any>(await api.get('/api/workers/me/open-shifts')).shifts || []) as JobShift[],
  getRecommendedShifts: async () => (data<any>(await api.get('/api/workers/me/open-shifts/recommended')).shifts || []) as JobShift[],
  getApplications: async () => (data<any>(await api.get('/api/workers/me/applications')).applications || []) as ShiftApplication[],
  apply: async (shiftId: string) => data<any>(await api.post(`/api/workers/me/open-shifts/${shiftId}/apply`, {})),
  withdraw: async (applicationId: string) => data<any>(await api.post(`/api/workers/me/shift-applications/${applicationId}/withdraw`, {})),
  getAvailability: async () => (data<any>(await api.get('/api/workers/me/availability')).availability || []) as WorkerAvailabilityWindow[],
  createAvailability: async (payload: Record<string, unknown>) => data<any>(await api.post('/api/workers/me/availability', payload)),
  updateAvailability: async (id: string, payload: Record<string, unknown>) => data<any>(await api.patch(`/api/workers/me/availability/${id}`, payload)),
  deleteAvailability: async (id: string) => data<any>(await api.delete(`/api/workers/me/availability/${id}`)),
  getUnavailability: async () => (data<any>(await api.get('/api/workers/me/unavailability')).unavailability || []) as WorkerUnavailabilityWindow[],
  createUnavailability: async (payload: Record<string, unknown>) => data<any>(await api.post('/api/workers/me/unavailability', payload)),
  deleteUnavailability: async (id: string) => data<any>(await api.delete(`/api/workers/me/unavailability/${id}`)),
  getProfile: async () => data<any>(await api.get('/api/workers/me/profile')).profile as WorkerProfileDetail,
  updateProfile: async (payload: Record<string, unknown>) => data<any>(await api.patch('/api/workers/me/profile', payload)).profile as WorkerProfileDetail,
  getReliability: async () => data<any>(await api.get('/api/workers/me/reliability')).reliability as WorkerReliabilityProfile | null,
  getDocuments: async () => (data<any>(await api.get('/api/workers/me/documents')).documents || []) as WorkerProfileDocument[],
  uploadDocument: async (form: FormData) => data<any>(await api.post('/api/workers/me/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } })),
  removeDocument: async (id: string) => data<any>(await api.delete(`/api/workers/me/documents/${id}`)),
  confirmExtraction: async (id: string, extractedFields: Record<string, unknown>) => data<any>(await api.post(`/api/workers/me/documents/${id}/extraction-confirmation`, { extractedFields })),
  getTimesheets: async () => (data<any>(await api.get('/api/workers/me/timesheets')).timesheets || []) as WorkerTimesheet[],
  acceptTimesheet: async (shiftId: string) => data<any>(await api.post(`/api/workers/me/timesheets/${shiftId}/accept-adjustment`, {})),
  disputeTimesheet: async (shiftId: string, workerDisputeMessage: string) => data<any>(await api.post(`/api/workers/me/timesheets/${shiftId}/dispute`, { workerDisputeMessage })),
  getThreads: async () => (data<any>(await api.get('/api/workers/me/messages/threads')).threads || []) as WorkerMessageThread[],
  getMessages: async (threadId: string) => (data<any>(await api.get(`/api/workers/me/messages/threads/${threadId}/messages`)).messages || []) as WorkerMessage[],
  sendMessage: async (threadId: string, body: string) => data<any>(await api.post(`/api/workers/me/messages/threads/${threadId}/messages`, { body })),
  markThreadRead: async (threadId: string) => data<any>(await api.patch(`/api/workers/me/messages/threads/${threadId}/read`, {})),
  getNotifications: async () => (data<any>(await api.get('/api/notifications')).notifications || []) as WorkerNotification[],
  getDecisions: async () => (data<any>(await api.get('/api/decisions/me')).decisions || []) as WorkerDecision[],
  getDecisionAppeals: async () => (data<any>(await api.get('/api/decisions/me/appeals')).appeals || []) as WorkerDecisionAppeal[],
  submitDecisionAppeal: async (decisionId: string, workerMessage: string) => data<any>(await api.post(`/api/decisions/me/${decisionId}/appeals`, { workerMessage })),
  getLocationPrivacyStatus: async () => data<any>(await api.get('/api/workers/me/location-privacy/status')).status as WorkerLocationPrivacyStatus,
  markNotificationRead: async (id: string) => data<any>(await api.patch(`/api/notifications/${id}/read`, {})),
  markAllNotificationsRead: async () => data<any>(await api.patch('/api/notifications/read-all', {})),
  getSkills: async () => (data<any>(await api.get('/api/workers/me/canonical-skills')).skills || []) as Array<Record<string, any>>,
  getSkillProgression: async () => (data<any>(await api.get('/api/workers/me/skills/progression')).skills || []) as WorkerSkillProgression[],
  addSkill: async (skillId: string, level?: string) => data<any>(await api.post('/api/workers/me/canonical-skills', { skillId, level })),
  removeSkill: async (skillId: string) => data<any>(await api.delete(`/api/workers/me/canonical-skills/${skillId}`)),
  markOnWay: async (assignmentId: string, coords?: GeolocationCoordinates) => data<any>(await api.post(`/api/workers/me/assignments/${assignmentId}/on-the-way`, coords ? { lat: coords.latitude, lon: coords.longitude, accuracyMeters: coords.accuracy } : {})),
  reportAttendance: async (assignmentId: string, reportType: 'running_late' | 'cannot_attend' | 'request_cancellation', notes: string) => data<any>(await api.post(`/api/workers/me/assignments/${assignmentId}/attendance-report`, { reportType, notes })),
  punch: async (action: 'punch-in' | 'punch-out' | 'break-start' | 'break-end', assignmentId: string, locationId?: string | null, coords?: GeolocationCoordinates) => {
    const payload: Record<string, unknown> = { jobAssignmentId: assignmentId, source: 'web', idempotencyKey: idempotencyKey(action, assignmentId) }
    if (locationId) payload.locationId = locationId
    if (coords) Object.assign(payload, { lat: coords.latitude, lng: coords.longitude, accuracyMeters: coords.accuracy })
    return data<any>(await api.post(`/api/workers/me/${action}`, payload, { headers: { 'Idempotency-Key': String(payload.idempotencyKey) } }))
  },
}
